import { toPng } from "html-to-image"
import { Code2, Plus, Quote } from "lucide-react"
import { useCallback, useMemo, useRef, useState } from "react"

import StoryPlatformPicker from "@/components/stories/story-platform-picker"
import { Button } from "@/components/ui/button"
import { useTimeZone } from "@/hooks/use-time-zone"
import { zonedWallClockToIso } from "@/lib/scheduling"
import type {
  CodeTake,
  MentionHandleGroup,
  Platform,
  PlatformCredentials,
  QuotableTake,
  QuoteTake,
  Take,
  YoutubeCategory,
} from "@/types"

import {
  CODE_TAKE_TYPE,
  DEFAULT_TAKE_TYPE,
  QUOTE_TAKE_TYPE,
  type StoryFormData,
  type StoryTakesAttributes,
  existingMediaCompositeKey,
  mediaFileCompositeKey,
  normalizeTakePositions,
  selectedPlatformsFromSyndications,
} from "./story-form-utils"
import TakeCard, { useTakeManagement } from "./take-card"
import { useAltTextGeneration } from "./use-alt-text-generation"

export type {
  StoryFormData,
  StorySyndicationsAttributes,
  StoryTakeAttribute,
  StoryTakesAttributes,
  SyndicationAttribute,
  TakeMediaFileAttribute,
  TakeMediaFilesAttributes,
} from "./story-form-utils"

interface Props {
  data: StoryFormData
  errors: Partial<Record<string, string>>
  processing: boolean
  existingTakes?: (Take | CodeTake | QuoteTake)[]
  lockedKeys?: string[]
  lockedStatusLabels?: Record<string, string>
  availablePlatforms?: Platform[]
  currentStoryId?: number
  platformCredentials?: PlatformCredentials
  mentionHandleGroups?: MentionHandleGroup[]
  mentionPlatforms?: Platform[]
  mastodonServerDomain?: string | null
  youtubeCategories: YoutubeCategory[]
  defaultYoutubeCategoryId: number
  onChange: (value: StoryFormData) => void
  onSubmit: (e: React.FormEvent, value: StoryFormData) => void | Promise<void>
  onCancel: () => void
}

// Rails accepts_nested_attributes_for requires a hash keyed by index, not an array,
// when mixing File uploads with other fields, otherwise FormData serialization
// causes _destroy to bleed across entries.
export default function StoryForm({
  data,
  errors,
  processing,
  existingTakes = [],
  lockedKeys = [],
  lockedStatusLabels = {},
  availablePlatforms = [],
  currentStoryId,
  platformCredentials,
  mentionHandleGroups = [],
  mentionPlatforms = [],
  mastodonServerDomain,
  youtubeCategories,
  defaultYoutubeCategoryId,
  onChange,
  onSubmit,
  onCancel,
}: Props) {
  const timeZone = useTimeZone()
  const codePreviewRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const [renderingCodeImages, setRenderingCodeImages] = useState(false)
  const [localMentionGroups, setLocalMentionGroups] =
    useState(mentionHandleGroups)

  const handleMentionGroupSaved = useCallback(
    (savedGroup: MentionHandleGroup) => {
      setLocalMentionGroups((prev) => {
        const idx = prev.findIndex((g) => g.id === savedGroup.id)
        if (idx >= 0) {
          const next = [...prev]
          next[idx] = savedGroup
          return next
        }
        return [...prev, savedGroup]
      })
    },
    [],
  )

  const {
    takeEntries,
    addTake,
    removeTake,
    moveTake,
    handleFiles,
    removeExistingMedia,
    removeNewMedia,
    updateExistingAltText,
    updateNewAltText,
    updateTakeBody,
    updateCodeLanguage,
    updateCodeSnippet,
    updateQuotedTake,
  } = useTakeManagement(data, onChange)

  const selectedPlatforms = useMemo(
    () =>
      selectedPlatformsFromSyndications(
        data.syndications_attributes,
        availablePlatforms,
      ),
    [data.syndications_attributes, availablePlatforms],
  )

  const dataRef = useRef(data)
  dataRef.current = data

  const getCurrentAltText = useCallback(
    (takeKey: number, mediaKey: number) =>
      dataRef.current.takes_attributes[takeKey]?.take_media_files_attributes[
        mediaKey
      ]?.alt_text,
    [],
  )

  const takeBody = useCallback(
    (takeKey: number) => dataRef.current.takes_attributes[takeKey]?.body,
    [],
  )

  // The alt-text result arrives asynchronously over ActionCable, long after the
  // request is made. Route the writes through refs so we always apply to the
  // latest form data — capturing the updaters at request time would stomp the
  // form with a stale snapshot (e.g. wiping the image that was just added).
  const updateNewAltTextRef = useRef(updateNewAltText)
  updateNewAltTextRef.current = updateNewAltText
  const updateExistingAltTextRef = useRef(updateExistingAltText)
  updateExistingAltTextRef.current = updateExistingAltText

  const { requestAltText, generatingKeys } = useAltTextGeneration()

  const generateForFile = useCallback(
    (takeKey: number, mediaKey: number, file: File) => {
      requestAltText({
        key: mediaFileCompositeKey(takeKey, mediaKey),
        file,
        body: takeBody(takeKey),
        shouldApply: () => !getCurrentAltText(takeKey, mediaKey),
        onResult: (altText) =>
          updateNewAltTextRef.current(takeKey, mediaKey, altText),
      })
    },
    [requestAltText, takeBody, getCurrentAltText],
  )

  const regenerateNewAltText = useCallback(
    (takeKey: number, mediaKey: number) => {
      const file =
        dataRef.current.takes_attributes[takeKey]?.take_media_files_attributes[
          mediaKey
        ]?.media_file
      if (!file) return

      requestAltText({
        key: mediaFileCompositeKey(takeKey, mediaKey),
        file,
        body: takeBody(takeKey),
        shouldApply: () => true,
        onResult: (altText) =>
          updateNewAltTextRef.current(takeKey, mediaKey, altText),
      })
    },
    [requestAltText, takeBody],
  )

  const regenerateExistingAltText = useCallback(
    async (takeKey: number, mediaId: number, url: string) => {
      const response = await fetch(url)
      const blob = await response.blob()
      const file = new File([blob], "image", { type: blob.type })

      requestAltText({
        key: existingMediaCompositeKey(takeKey, mediaId),
        file,
        body: takeBody(takeKey),
        shouldApply: () => true,
        onResult: (altText) =>
          updateExistingAltTextRef.current(takeKey, mediaId, altText),
      })
    },
    [requestAltText, takeBody],
  )

  const formError =
    errors.takes ??
    errors.take_media_files ??
    errors["takes.take_media_files"] ??
    errors.base

  async function buildSubmitValue() {
    const nextTakes: StoryTakesAttributes = {}

    for (const [key, take] of Object.entries(data.takes_attributes)) {
      const takeKey = Number(key)
      if (take.type !== CODE_TAKE_TYPE || take._destroy) {
        nextTakes[takeKey] = take
        continue
      }

      const node = codePreviewRefs.current[takeKey]
      if (!node || !take.code_snippet?.trim()) {
        nextTakes[takeKey] = take
        continue
      }

      const codeImageDataUrl = await toPng(node, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#020617",
      })

      nextTakes[takeKey] = { ...take, code_image_data_url: codeImageDataUrl }
    }

    const publishAtIso = data.scheduled_at
      ? (zonedWallClockToIso(data.scheduled_at, timeZone) ?? undefined)
      : undefined

    return {
      title: data.title,
      tags: data.tags,
      scheduled_at: publishAtIso,
      syndications_attributes: data.syndications_attributes,
      takes_attributes: normalizeTakePositions(nextTakes),
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setRenderingCodeImages(true)

    try {
      const nextValue = await buildSubmitValue()
      onChange(nextValue)
      await onSubmit(e, nextValue)
    } finally {
      setRenderingCodeImages(false)
    }
  }

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(e)
      }}
      className="flex flex-col gap-6 pb-24 md:pb-0"
    >
      {formError && <p className="text-destructive text-sm">{formError}</p>}

      <div className="flex flex-col gap-4">
        {takeEntries.length === 0 && (
          <p className="text-muted-foreground py-8 text-center text-sm">
            Each take becomes a post in a thread. On platforms that don&apos;t
            support threads (like LinkedIn), all takes are combined into the
            first post.
          </p>
        )}

        {takeEntries.map(([takeKey, take], index) => {
          const existingTake = existingTakes.find((t) => t.id === take.id)
          const initialSelectedTake: QuotableTake | null =
            existingTake?.type === QUOTE_TAKE_TYPE
              ? ((existingTake as QuoteTake).quoted_take ?? null)
              : null

          return (
            <TakeCard
              key={takeKey}
              takeKey={takeKey}
              take={take}
              index={index}
              totalTakes={takeEntries.length}
              existingTake={existingTake}
              selectedPlatforms={selectedPlatforms}
              excludeStoryId={currentStoryId}
              initialSelectedTake={initialSelectedTake}
              mentionHandleGroups={localMentionGroups}
              mentionPlatforms={mentionPlatforms}
              mastodonServerDomain={mastodonServerDomain}
              onMentionGroupSaved={handleMentionGroupSaved}
              onMove={(direction) => moveTake(takeKey, direction)}
              onRemove={() => removeTake(takeKey)}
              onUpdateBody={(body) => updateTakeBody(takeKey, body)}
              onUpdateQuotedTake={(quotedTakeId) =>
                updateQuotedTake(takeKey, quotedTakeId)
              }
              onUpdateCodeLanguage={(lang) => updateCodeLanguage(takeKey, lang)}
              onUpdateCodeSnippet={(snip) => updateCodeSnippet(takeKey, snip)}
              generatingAltTextKeys={generatingKeys}
              onHandleFiles={(files, existingMedia, onLimitMessage) =>
                handleFiles(
                  takeKey,
                  files,
                  existingMedia,
                  onLimitMessage,
                  generateForFile,
                )
              }
              onRemoveExistingMedia={(mediaId) =>
                removeExistingMedia(takeKey, mediaId)
              }
              onRemoveNewMedia={(mediaKey) => removeNewMedia(takeKey, mediaKey)}
              onUpdateExistingAltText={(mediaId, altText) =>
                updateExistingAltText(takeKey, mediaId, altText)
              }
              onUpdateNewAltText={(mediaKey, altText) =>
                updateNewAltText(takeKey, mediaKey, altText)
              }
              onRegenerateExistingAltText={(mediaId, url) => {
                void regenerateExistingAltText(takeKey, mediaId, url)
              }}
              onRegenerateNewAltText={(mediaKey) =>
                regenerateNewAltText(takeKey, mediaKey)
              }
              codePreviewRef={(el) => {
                codePreviewRefs.current[takeKey] = el
              }}
            />
          )
        })}

        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => addTake(DEFAULT_TAKE_TYPE)}
          >
            <Plus className="size-4" />
            Add take
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => addTake(CODE_TAKE_TYPE)}
          >
            <Code2 className="size-4" />
            Add code take
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => addTake(QUOTE_TAKE_TYPE)}
          >
            <Quote className="size-4" />
            Add quote take
          </Button>
        </div>
      </div>

      <StoryPlatformPicker
        availablePlatforms={availablePlatforms}
        platformCredentials={platformCredentials}
        syndicationsAttributes={data.syndications_attributes}
        publishAt={data.scheduled_at ?? ""}
        onSyndicationsAttributesChange={(next) =>
          onChange({ ...data, syndications_attributes: next })
        }
        onPublishAtChange={(value) =>
          onChange({ ...data, scheduled_at: value })
        }
        platformsError={errors.syndications}
        publishAtError={errors.scheduled_at}
        lockedKeys={lockedKeys}
        lockedStatusLabels={lockedStatusLabels}
        youtubeCategories={youtubeCategories}
        defaultYoutubeCategoryId={defaultYoutubeCategoryId}
        title={data.title ?? ""}
        tags={data.tags ?? []}
        onTitleChange={(value) => onChange({ ...data, title: value })}
        onTagsChange={(value) => onChange({ ...data, tags: value })}
      />

      <div
        className={[
          "bg-background/95 fixed inset-x-0 bottom-0 z-20 flex gap-3 border-t px-6 py-3 backdrop-blur",
          "pb-[max(env(safe-area-inset-bottom),0.75rem)]",
          "md:static md:inset-x-auto md:bottom-auto md:z-auto md:border-0 md:bg-transparent md:px-0 md:py-0 md:pt-2 md:backdrop-blur-none",
        ].join(" ")}
      >
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex-1 md:flex-none"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={processing || renderingCodeImages}
          className="flex-1 md:flex-none"
        >
          Save Draft
        </Button>
      </div>
    </form>
  )
}
